import { useState, useCallback, useRef } from 'react';
import { ChatMessage, Menu } from '../types/menu';
import api from '../services/api';
import { trackChatMessageSent, trackChatResponseReceived, tagClarityWithQuery, getClaritySessionUrl } from '../utils/analytics';

export const useMenuChat = (menu: Menu | null, currentLanguage: string = 'en') => {
  // State management
  const [messages, setMessages] = useState<ChatMessage[]>([
    {
      id: '1',
      text: `What would you like to know? 
`,
      sender: 'ai',
      timestamp: new Date()
    }
  ]);
  const [newMessage, setNewMessage] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasInitialMessageBeenSent, setHasInitialMessageBeenSent] = useState(false);

  // Stable thread ID for this chat session — generated once, survives re-renders
  const threadIdRef = useRef<string>(crypto.randomUUID());

  // Use refs to store the latest values without causing re-renders
  const messagesRef = useRef(messages);
  messagesRef.current = messages;
  
  const newMessageRef = useRef(newMessage);
  newMessageRef.current = newMessage;
  
  const menuIdRef = useRef(menu?.id);
  menuIdRef.current = menu?.id;
  
  const currentLanguageRef = useRef(currentLanguage);
  currentLanguageRef.current = currentLanguage;

  // Helper functions - memoized to prevent unnecessary re-computations
  const createMessage = useCallback((text: string, sender: 'user' | 'ai'): ChatMessage => ({
    id: Date.now().toString(),
    text,
    sender,
    timestamp: new Date()
  }), []);

  // Get previous messages without causing re-renders
  const getPreviousMessages = useCallback(() => {
    return messagesRef.current
      .filter(msg => msg.id !== '1') // Exclude the welcome message
      .map(msg => ({
        id: msg.id,
        text: msg.text,
        sender: msg.sender,
        timestamp: msg.timestamp
      }));
  }, []);

  const handleError = useCallback((err: unknown) => {
    console.error('Error sending message:', err);
    const errorMessage = err instanceof Error ? err.message : 'Failed to send message';
    setError(errorMessage);
    
    const errorChatMessage = createMessage(
      'Sorry, I encountered an error while processing your request. Please try again.',
      'ai'
    );
    setMessages(prev => [...prev, errorChatMessage]);
  }, [createMessage]);

  // Message handling functions - optimized to reduce dependencies
  // Use refs to avoid recreating callback on every keystroke
  const handleSendMessage = useCallback(async () => {
    const messageToSend = newMessageRef.current;
    if (!messageToSend.trim() || isSending) return;
    
    const userMessage = createMessage(messageToSend, 'user');
    setMessages(prev => [...prev, userMessage]);
    setNewMessage('');
    setIsSending(true);
    setError(null);
    const requestStartedAt = performance.now();
    const menuId = menuIdRef.current || '';
    const currentLanguage = currentLanguageRef.current;

    trackChatMessageSent({
      menuId,
      language: currentLanguage,
      contextType: 'menu',
      messageLength: userMessage.text.length
    });
    tagClarityWithQuery(userMessage.text, menuId);

    try {
      const response = await api.post('/query/menu', {
        user_query: userMessage.text,
        menu_id: menuIdRef.current || '',
        previous_messages: getPreviousMessages(),
        current_language: currentLanguageRef.current,
        clarity_session_url: getClaritySessionUrl(),
        thread_id: threadIdRef.current
      });

      const aiMessage = createMessage(response.data.message, 'ai');
      setMessages(prev => [...prev, aiMessage]);
      trackChatResponseReceived({
        menuId,
        language: currentLanguage,
        contextType: 'menu',
        latencyMs: Math.round(performance.now() - requestStartedAt)
      });
    } catch (err) {
      handleError(err);
    } finally {
      setIsSending(false);
    }
  }, [isSending, createMessage, getPreviousMessages, handleError]);

  const sendInitialMessage = useCallback(async () => {
    if (hasInitialMessageBeenSent || isSending) return;
    
    try {
      setLoading(true);
      await handleSendMessage();
      setHasInitialMessageBeenSent(true);
    } catch (err) {
      handleError(err);
    } finally {
      setLoading(false);
    }
  }, [hasInitialMessageBeenSent, isSending, handleSendMessage, handleError]);

  const sendDirectMessage = useCallback(async (text: string) => {
    if (isSending) return;
    
    const userMessage = createMessage(text, 'user');
    setMessages(prev => [...prev, userMessage]);
    setIsSending(true);
    setError(null);
    const requestStartedAt = performance.now();
    const menuId = menuIdRef.current || '';
    const currentLanguage = currentLanguageRef.current;

    trackChatMessageSent({
      menuId,
      language: currentLanguage,
      contextType: 'menu',
      messageLength: text.length
    });
    tagClarityWithQuery(text, menuId);

    try {
      const response = await api.post('/query/menu', {
        user_query: text,
        menu_id: menuIdRef.current || '',
        previous_messages: getPreviousMessages(),
        current_language: currentLanguageRef.current,
        clarity_session_url: getClaritySessionUrl(),
        thread_id: threadIdRef.current
      });

      const aiMessage = createMessage(response.data.message, 'ai');
      setMessages(prev => [...prev, aiMessage]);
      trackChatResponseReceived({
        menuId,
        language: currentLanguage,
        contextType: 'menu',
        latencyMs: Math.round(performance.now() - requestStartedAt)
      });
    } catch (err) {
      handleError(err);
    } finally {
      setIsSending(false);
    }
  }, [isSending, createMessage, getPreviousMessages, handleError]);

  return {
    messages,
    newMessage,
    setNewMessage,
    handleSendMessage,
    sendDirectMessage,
    isSending,
    loading,
    error,
    sendInitialMessage
  };
}; 